import React, { useEffect, useState } from "react";
import { Plus, Filter, Trash2 } from "lucide-react";
import LabShell from "./LabShell";
import { Panel, StatusPill, EmptyState, formatRelative, formatDuration } from "./labUi";
import {
    fetchRuns,
    fetchInbox,
    deleteInboxItem,
    normalizeRunStatus,
    isBackendUnavailable,
} from "../../services/LabService";
import { labHref, navigate } from "./labRouter";

const STATUS_FILTERS = ["all", "queued", "running", "done", "failed", "cancelled"];

const rowsFromRuns = (runs = []) =>
    runs.map((r) => ({
        key: r.run_id,
        runId: r.run_id,
        title: r.proposal_id || r.run_id,
        subtitle: r.run_id,
        status: normalizeRunStatus(r.status),
        startedAt: r.started_at,
        endedAt: r.stopped_at,
        durationMs: (r.duration_seconds || 0) * 1000,
        currentState: r.current_state,
        reportAvailable: r.report_available,
        clickable: true,
    }));

const rowsFromPending = (items = []) =>
    items.map((p) => ({
        key: `inbox:${p.proposal_id}`,
        proposalId: p.proposal_id,
        title: p.title || p.proposal_id,
        subtitle: `${p.proposal_id} · ${p.scenario_count ?? "?"} scenarios · ~$${p.cost_estimate_usd?.toFixed?.(2) || "—"}`,
        status: "queued",
        startedAt: p.created_at,
        endedAt: null,
        durationMs: null,
        currentState: null,
        reportAvailable: false,
        clickable: false,
        withdrawable: true,
    }));

const LabExperimentsList = () => {
    const [statusFilter, setStatusFilter] = useState("all");
    const [data, setData] = useState({ loading: true, rows: [], error: null });

    const reload = async () => {
        setData((s) => ({ ...s, loading: true }));
        try {
            const sfnFilter = {
                running: "RUNNING",
                done: "SUCCEEDED",
                failed: "FAILED",
                cancelled: "ABORTED",
            }[statusFilter];
            // Always include pending unless the filter excludes "queued"
            const includePending = statusFilter === "all" || statusFilter === "queued";
            const includeRuns = statusFilter !== "queued";

            const [runRes, inboxRes] = await Promise.all([
                includeRuns
                    ? fetchRuns({ status: sfnFilter, limit: 100 }).catch((e) => (isBackendUnavailable(e) ? { items: [] } : Promise.reject(e)))
                    : Promise.resolve({ items: [] }),
                includePending
                    ? fetchInbox({ status: "pending", limit: 50 }).catch((e) => (isBackendUnavailable(e) ? { items: [] } : Promise.reject(e)))
                    : Promise.resolve({ items: [] }),
            ]);

            const combined = [...rowsFromPending(inboxRes?.items || []), ...rowsFromRuns(runRes?.items || [])];
            setData({ loading: false, rows: combined, error: null });
        } catch (err) {
            setData({ loading: false, rows: [], error: err });
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await reload(); })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const handleWithdraw = async (proposalId) => {
        if (!window.confirm(`Withdraw proposal "${proposalId}"?`)) return;
        try {
            await deleteInboxItem(proposalId);
            reload();
        } catch (err) {
            alert(err.message);
        }
    };

    return (
        <LabShell
            activeKey="experiments"
            headerAction={
                <button
                    onClick={() => navigate(labHref("submit"))}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                    <Plus className="w-4 h-4" /> New experiment
                </button>
            }
        >
            <Panel
                title={
                    <span className="flex items-center gap-2">
                        <Filter className="w-3.5 h-3.5" /> Experiments
                    </span>
                }
                padded={false}
            >
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1">
                        {STATUS_FILTERS.map((s) => (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                                    statusFilter === s
                                        ? "bg-indigo-600 text-white border-indigo-600"
                                        : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto text-xs text-slate-500">{data.rows.length} result{data.rows.length === 1 ? "" : "s"}</div>
                </div>

                {data.loading ? (
                    <div className="px-4 py-8 text-center text-sm text-slate-500">Loading…</div>
                ) : data.error ? (
                    <div className="px-4 py-8 text-center text-sm text-red-600">{data.error.message}</div>
                ) : data.rows.length === 0 ? (
                    <EmptyState
                        title="No items match"
                        body="Adjust the filters or submit a new experiment."
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-xs text-slate-500 uppercase tracking-wide">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Proposal / run</th>
                                    <th className="text-left px-4 py-2 font-medium">Status</th>
                                    <th className="text-left px-4 py-2 font-medium">Last state</th>
                                    <th className="text-left px-4 py-2 font-medium">Started</th>
                                    <th className="text-left px-4 py-2 font-medium">Ended</th>
                                    <th className="text-right px-4 py-2 font-medium">Duration</th>
                                    <th className="text-right px-4 py-2 font-medium"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map((row) => (
                                    <tr
                                        key={row.key}
                                        onClick={() => row.clickable && navigate(labHref(`experiments/${row.runId}`))}
                                        className={`border-b border-slate-100 dark:border-slate-800 ${
                                            row.clickable ? "hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer" : ""
                                        }`}
                                    >
                                        <td className="px-4 py-2.5 font-medium">
                                            {row.title}
                                            <div className="text-xs text-slate-500 font-mono">{row.subtitle}</div>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <StatusPill status={row.status} />
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 font-mono text-xs">
                                            {row.currentState || "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{formatRelative(row.startedAt)}</td>
                                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{formatRelative(row.endedAt)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono">
                                            {row.durationMs ? formatDuration(row.durationMs) : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            {row.withdrawable ? (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleWithdraw(row.proposalId); }}
                                                    title="Withdraw"
                                                    className="text-slate-400 hover:text-red-500"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </LabShell>
    );
};

export default LabExperimentsList;
